import { useState } from 'react';
import { useEventContext, useQuizContext, useUserContext } from 'contexts';
import { updateQuiz } from 'graphql/mutations';
import { UpdateQuiz, UpdateQuizVariables } from 'graphql/mutations/types/UpdateQuiz';
import { ApolloError, useMutation } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import { NavPages, NavUtils } from 'utils';
import { ConfirmDialog, EditQuiz, EditQuizQuestions, EditQuizStyles, useSnackbar } from 'components';
import { Quiz } from 'types';
import omit from 'lodash/omit';
import { Box, Tab, Tabs } from '@mui/material';
import { QuizTypes } from 'consts';
import { JeopardyQuizCreator } from '../jeopardy-quiz';
import { deleteQuizById, QuizApiError } from './quiz-api';

enum EditSections {
    Details = 0,
    Style,
    Questions
}

export const EditQuizPage = () => {
    const [selectedTab, setSelectedTab] = useState(EditSections.Details);
    const [deletingQuiz, setDeletingQuiz] = useState(false);
    const { quiz, refetch: refetchQuiz } = useQuizContext();
    const snackbar = useSnackbar();
    const navigate = useNavigate();
    const event = useEventContext();
    const user = useUserContext();
    const { eventAlias } = useParams();

    const onQuizUpdated = (data: UpdateQuiz) => {
        if (!data.update_quiz_master_quizzes_by_pk) {
            snackbar.error('Failed to update quiz. Please refresh and try again.')
            return;
        }
        refetchQuiz();
        snackbar.success('Quiz updated')
    };

    const onQuizUpdateError = (error: ApolloError) => {
        snackbar.error('Failed to update quiz. Please refresh and try again.')
        console.error('Error updating quiz ', error);
    };

    const [updateQuizMutation] = useMutation<UpdateQuiz, UpdateQuizVariables>(updateQuiz, {
        onCompleted: onQuizUpdated,
        onError: onQuizUpdateError
    });

    if (!user.isQuizCreatorOrEventAdmin(quiz.id!, eventAlias)) {
        navigate(NavUtils.generateNavPath(NavPages.Root, undefined, event?.alias));
        return null;
    }

    const onSaveQuiz = async (quiz: Quiz) => {
        await updateQuizMutation({
            variables: {
                id: quiz.id!,
                ...omit(quiz, ['createdAt', 'id', 'authorUserId', 'styles'])
            }
        });
    };

    const onStylesChange = () => {
        refetchQuiz();
    }

    const onSelectTab = (event: React.SyntheticEvent, newValue: EditSections) => {
        setSelectedTab(newValue);
    };

    const onBackToQuiz = () => {
        navigate(NavUtils.generateNavPath(NavPages.Quiz, quiz.id, event?.alias))
    }

    const onDeleteQuiz = async () => {
        try {
            setDeletingQuiz(true);
            await deleteQuizById(quiz.id!);
            snackbar.success('Quiz deleted');
            navigate(NavUtils.getQuizSelectorPath(event?.alias));
        } catch (error) {
            const message = error instanceof QuizApiError ? error.message : 'Failed to delete quiz. Please try again.';
            snackbar.error(message);
        } finally {
            setDeletingQuiz(false);
        }
    };

    function a11yProps(index: number) {
        return {
            id: `vertical-tab-${index}`,
            'aria-controls': `vertical-tabpanel-${index}`
        };
    }

    const isJeopardy = quiz.quizTypeId === QuizTypes.Jeopardy;
    const canDeleteQuiz = user.isSuperAdmin() || user.id === quiz.authorUserId;

    const scenes: Record<number, JSX.Element> = {
        [EditSections.Details]: (
            <ConfirmDialog title="Delete quiz" message="Are you sure you want to delete this quiz? This also removes its sessions and related quiz data.">
                {(confirm) => (
                    <EditQuiz
                        onSaveQuiz={onSaveQuiz}
                        initQuiz={quiz}
                        canDeleteQuiz={canDeleteQuiz}
                        deletingQuiz={deletingQuiz}
                        onDeleteQuiz={confirm(onDeleteQuiz)}
                    />
                )}
            </ConfirmDialog>
        ),
        [EditSections.Style]: <EditQuizStyles quiz={quiz} onStylesChange={onStylesChange} />,
        [EditSections.Questions]: isJeopardy ? <JeopardyQuizCreator quizId={quiz.id!} /> : <EditQuizQuestions />,
    };

    return (
        <div className="aq-content">
            <div className="page-title">Edit Quiz - {quiz.name}</div>
            <Box className='edit-quiz-tabs-wrapper'>
                <Tabs
                    orientation="vertical"
                    variant="scrollable"
                    value={selectedTab}
                    onChange={onSelectTab}
                    aria-label="Edit Quiz Tabs"
                    className='edit-quiz-tabs'
                    sx={{ borderRight: 1, borderColor: 'divider' }}
                >
                    <Tab label="Details" {...a11yProps(0)} value={EditSections.Details} />
                    <Tab label="Styles" {...a11yProps(1)} value={EditSections.Style} />
                    <Tab label={isJeopardy ? 'Board' : 'Questions'} {...a11yProps(2)} value={EditSections.Questions} />
                    <Tab label="Back to quiz" {...a11yProps(3)} style={{textDecoration: 'underline'}} onClick={onBackToQuiz} />
                </Tabs>
            
                <div className='edit-quiz-tab-content'>
                    {scenes[selectedTab]}
                </div>
            </Box>
        </div>
    );
};
